/**
 * @prettier
 */
import { IconButton } from '@material-ui/core';
import Grid from '@material-ui/core/Grid';
import Tooltip from '@material-ui/core/Tooltip';
import CheckIcon from '@material-ui/icons/Check';
import CloseIcon from '@material-ui/icons/Close';
import DeleteIcon from '@material-ui/icons/Delete';
import { MUIDataTableColumn } from 'mui-datatables';
import { useState } from 'react';
import * as React from 'react';
import { findOwnFleetDriversByRestaurantIdApi } from 'src/api/letseatmanager/ownFleet/findOwnFleetDriversByRestaurantIdApi';
import { CreateToolbarButton } from 'src/components/mui-datatables/CreateToolbarButton';
import { RefreshToolbarButton } from 'src/components/mui-datatables/RefreshToolbarButton';
import { SecuredAndSubscribedPage } from 'src/components/page/SecuredAndSubscribedPage';
import { Table } from 'src/components/Table';
import { UpdatingContentProgress } from 'src/components/UpdatingContentProgress';
import { OwnFleetDriverStatus, OwnFleetDriverStatuses } from 'src/constants/OwnFleetDriverStatus';
import { RolePermissions } from 'src/constants/RolePermission';
import { translate } from 'src/i18n/translate';
import { CreateExternalOwnFleetDriverDialog } from 'src/scenes/letseatadmin/driver/CreateExternalOwnFleetDriverDialog';
import { AcceptOwnFleetRequestDialog } from 'src/scenes/letseatmanager/ownFleetDrivers/AcceptOwnFleetRequestDialog';
import { RejectOwnFleetRequestDialog } from 'src/scenes/letseatmanager/ownFleetDrivers/RejectOwnFleetRequestDialog';
import { RemoveOwnFleetRequestDialog } from 'src/scenes/letseatmanager/ownFleetDrivers/RemoveOwnFleetRequestDialog';
import { useFormatAsRestaurantCurrencyNumber } from 'src/services/restaurant/useFormatAsRestaurantCurrencyNumber';
import { DriverId } from 'src/types/Id';
import { formatDateTimeString } from 'src/utils/date/formatDateTimeString';
import { useLoadApi } from 'src/utils/react/useLoadApi';
import { useSelector } from 'src/utils/react/useSelector';

export function OwnFleetDriversPage(): React.ReactElement {
    const formatAsCurrencyNumber = useFormatAsRestaurantCurrencyNumber();

    const [openAcceptOwnFleetRequestDialog, setOpenAcceptOwnFleetRequestDialog] = useState(false);
    const [openRejectOwnFleetRequestDialog, setOpenRejectOwnFleetRequestDialog] = useState(false);
    const [openRemoveOwnFleetRequestDialog, setOpenRemoveOwnFleetRequestDialog] = useState(false);
    const [openCreateExternalOwnFleetDriverDialog, setOpenCreateExternalOwnFleetDriverDialog] = useState(false);
    const [ownFleetDriverStatusRequest, setOwnFleetDriverStatusRequest] = useState<{ driverId?: DriverId; OwnFleetDriverStatus?: OwnFleetDriverStatus }>({});

    const restaurantId = useSelector((state) => state.app.restaurantId);

    const [loading, { drivers }, refreshOwnFleetDrivers] = useLoadApi(findOwnFleetDriversByRestaurantIdApi, { restaurantId }, { initialValue: { drivers: [] }, dependencies: [restaurantId] });

    const handleClickNew = () => {
        setOpenCreateExternalOwnFleetDriverDialog(true);
    };

    const ownFleetColumns: Array<MUIDataTableColumn> = [
        {
            name: 'driverId',
            label: 'ID',
            options: {
                display: 'excluded',
                filter: false,
            },
        },
        {
            name: 'driverName',
            label: translate('Name'),
            options: {
                filter: true,
            },
        },
        {
            name: 'mobileNumber',
            label: translate('Mobile Number'),
            options: {
                filter: true,
            },
        },
        {
            name: 'orders',
            label: translate('Deliveries'),
            options: {
                filter: false,
            },
        },
        {
            name: 'total',
            label: translate('Total'),
            options: {
                filter: false,
            },
        },
        {
            name: 'requestedAt',
            label: translate('Requested At'),
            options: {
                filter: false,
            },
        },
        {
            name: 'acceptedAt',
            label: translate('Accepted At'),
            options: {
                filter: false,
            },
        },
        {
            name: 'status',
            label: translate('Status'),
            options: {
                filter: false,
                customBodyRender: (status: OwnFleetDriverStatus) => (status ? translate(`OwnFleetDriverStatuses.${status}`) : ''),
            },
        },
        {
            name: 'status',
            options: {
                filter: false,
                customBodyRender: (status: OwnFleetDriverStatus, data: { rowData: Array<DriverId> }) => {
                    const DRIVER_ID_POSITION = 0;
                    const driverId = data.rowData[DRIVER_ID_POSITION];

                    return (
                        <div style={{ width: '100%', display: 'flex', justifyContent: 'center' }}>
                            {status === OwnFleetDriverStatuses.PENDING && (
                                <>
                                    <Tooltip title={translate('Accept')}>
                                        <IconButton
                                            onClick={() => {
                                                setOwnFleetDriverStatusRequest({
                                                    driverId,
                                                    OwnFleetDriverStatus: OwnFleetDriverStatuses.ACCEPTED,
                                                });
                                                setOpenAcceptOwnFleetRequestDialog(true);
                                            }}
                                        >
                                            <CheckIcon />
                                        </IconButton>
                                    </Tooltip>
                                    <Tooltip title={translate('Reject')}>
                                        <IconButton
                                            onClick={() => {
                                                setOwnFleetDriverStatusRequest({
                                                    driverId,
                                                    OwnFleetDriverStatus: OwnFleetDriverStatuses.REJECTED,
                                                });
                                                setOpenRejectOwnFleetRequestDialog(true);
                                            }}
                                        >
                                            <CloseIcon />
                                        </IconButton>
                                    </Tooltip>
                                </>
                            )}
                            {status === OwnFleetDriverStatuses.REJECTED && (
                                <Tooltip title={translate('Accept')}>
                                    <IconButton
                                        onClick={() => {
                                            setOwnFleetDriverStatusRequest({
                                                driverId,
                                                OwnFleetDriverStatus: OwnFleetDriverStatuses.ACCEPTED,
                                            });
                                            setOpenAcceptOwnFleetRequestDialog(true);
                                        }}
                                    >
                                        <CheckIcon />
                                    </IconButton>
                                </Tooltip>
                            )}
                            {status === OwnFleetDriverStatuses.ACCEPTED && (
                                <>
                                    <Tooltip title={translate('Remove')}>
                                        <IconButton
                                            /* @ts-ignore */
                                            color={'red'}
                                            onClick={() => {
                                                setOwnFleetDriverStatusRequest({
                                                    driverId,
                                                    OwnFleetDriverStatus: OwnFleetDriverStatuses.REJECTED,
                                                });
                                                setOpenRemoveOwnFleetRequestDialog(true);
                                            }}
                                        >
                                            <DeleteIcon />
                                        </IconButton>
                                    </Tooltip>
                                </>
                            )}
                        </div>
                    );
                },
            },
        },
    ];

    return (
        <SecuredAndSubscribedPage title={translate('Own Drivers')} rolePermission={RolePermissions.OWN_FLEET_PAGE}>
            <Grid container spacing={5}>
                <RemoveOwnFleetRequestDialog
                    open={openRemoveOwnFleetRequestDialog}
                    onClose={() => setOpenRemoveOwnFleetRequestDialog(false)}
                    onSuccess={refreshOwnFleetDrivers}
                    driverId={ownFleetDriverStatusRequest.driverId!}
                />
                <AcceptOwnFleetRequestDialog
                    open={openAcceptOwnFleetRequestDialog}
                    onClose={() => setOpenAcceptOwnFleetRequestDialog(false)}
                    onSuccess={refreshOwnFleetDrivers}
                    driverId={ownFleetDriverStatusRequest.driverId!}
                />
                <RejectOwnFleetRequestDialog
                    open={openRejectOwnFleetRequestDialog}
                    onClose={() => setOpenRejectOwnFleetRequestDialog(false)}
                    onSuccess={refreshOwnFleetDrivers}
                    driverId={ownFleetDriverStatusRequest.driverId!}
                />
                <CreateExternalOwnFleetDriverDialog
                    open={openCreateExternalOwnFleetDriverDialog}
                    onClose={() => setOpenCreateExternalOwnFleetDriverDialog(false)}
                    onSuccess={refreshOwnFleetDrivers}
                    restaurantId={restaurantId}
                />
                <Grid item xs={12}>
                    <div style={{ position: 'relative', paddingBottom: 3 }}>
                        <UpdatingContentProgress loading={loading} bottom />
                        <Table
                            title={undefined}
                            data={drivers.map((ownFleetDriver) => {
                                const ownRestaurant = ownFleetDriver.linkedRestaurants?.find((linkedRestaurant) => linkedRestaurant.restaurantId === restaurantId);
                                return {
                                    driverId: ownFleetDriver.driverId,
                                    driverName: `${ownFleetDriver.firstName ?? ''} ${ownFleetDriver?.lastName ?? ''}`,
                                    mobileNumber: ownFleetDriver.mobileNumber,
                                    orders: ownFleetDriver.orders,
                                    total: formatAsCurrencyNumber(ownFleetDriver.total),
                                    status: ownRestaurant?.ownFleetDriverStatus,
                                    requestedAt: formatDateTimeString(ownRestaurant?.requestedAt),
                                    acceptedAt: formatDateTimeString(ownRestaurant?.acceptedAt),
                                };
                            })}
                            columns={ownFleetColumns}
                            options={{
                                responsive: 'standard',
                                tableBodyMaxHeight: '100%',
                                selectableRows: 'none',
                                filter: false,
                                download: false,
                                rowsPerPage: 100,
                                viewColumns: false,
                                print: false,
                                customToolbar: () => (
                                    <>
                                        <RefreshToolbarButton onClick={refreshOwnFleetDrivers} />
                                        <CreateToolbarButton onClick={handleClickNew} />
                                    </>
                                ),
                            }}
                        />
                    </div>
                </Grid>
            </Grid>
        </SecuredAndSubscribedPage>
    );
}
